import { api } from '@core/baseApi';
import { END_POINTS } from '@config';

export interface RecentChatThread {
  id: string;
  type: string;
  name: string;
  participantIds: string[];
  createdAt: string;
  lastMessage?: string;
  updatedAt: string;
  hasUnread?: boolean;
  avatar?: string | null;
  userAccessLevels: string[];
}

export interface RecentChatsResponse {
  data: {
    threads: RecentChatThread[];
    hasUnread: boolean;
  };
  message: string;
  statusCode: number;
}

export interface Message {
  id: string;
  content: string;
  senderId: string;
  threadId: string;
  createdAt: string;
  updatedAt: string;
  readBy?: string[];
  type?: 'text' | 'image' | 'file';
  metadata?: Record<string, unknown>;
  isSending?: boolean; // For optimistic updates
}

export interface MessagesResponse {
  data: {
    messages: Message[];
    hasMore: boolean;
    nextCursor?: string;
  };
  message: string;
  statusCode: number;
}

export interface ThreadResponse {
  data: {
    id: string;
    type: string;
    participants: Array<{
      id: string;
      name: string;
      avatar: string | null;
    }>;
    createdAt: string;
    updatedAt: string;
  };
  message: string;
  statusCode: number;
}

export interface Contact {
  className?: string;
  id: string;
  name: string;
  avatar: string | null;
  userAccessLevels: string[];
  hasUnread?: boolean;
}

export interface ContactsResponse {
  data: {
    contacts: Contact[];
    count: number;
    hasNext: boolean;
  };
  message: string;
  statusCode: number;
}

// Interface for send message request
interface SendMessageRequest {
  threadId: string;
  content: string;
  timezone: string;
}

// Interface for send message response
interface SendMessageResponse {
  data: Message;
  message: string;
  statusCode: number;
}

// Interface for mark messages as seen request
interface MarkMessagesSeenRequest {
  threadId: string;
  messageIds?: string[]; // Optional: specific message IDs to mark as seen
}

// Interface for mark messages as seen response
interface MarkMessagesSeenResponse {
  data: any;
  message: string;
  statusCode: number;
}

// Define the chat threads query
const enhancedApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Send a new message
    sendMessage: builder.mutation<SendMessageResponse, SendMessageRequest>({
      query: ({ threadId, ...body }) => ({
        url: `chat/threads/${threadId}/messages`,
        method: 'POST',
        body
      }),
      // Update the cache after a new message is sent
      async onQueryStarted({ threadId }, { dispatch, queryFulfilled }) {
        // Optimistic update: Add a temporary message
        const tempMessage: Message = {
          id: `temp-${Date.now()}`,
          content: 'Sending...',
          senderId: '', // Will be updated when the request completes
          threadId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          type: 'text',
          isSending: true
        };

        // Type for the cache update
        type CacheUpdate<T> = (draft: T) => T | void;

        // Add the temporary message to the cache
        const patchResult = dispatch(
          api.util.updateQueryData(
            'getThreadMessages' as never,
            { threadId } as never,
            ((draft: any) => {
              if (draft?.data?.messages) {
                draft.data.messages.push(tempMessage);
              }
              return draft;
            }) as CacheUpdate<any>
          )
        );

        try {
          const { data: newMessage } = await queryFulfilled;

          // Update the cache with the actual message from the server
          dispatch(
            api.util.updateQueryData(
              'getThreadMessages' as never,
              { threadId } as never,
              ((draft: any) => {
                if (draft?.data?.messages) {
                  // Remove any temporary messages for this thread
                  draft.data.messages = draft.data.messages.filter(
                    (msg: any) =>
                      !(msg.id.startsWith('temp-') && msg.threadId === threadId)
                  );
                  // Add the actual message
                  draft.data.messages.push(newMessage.data);
                }
                return draft;
              }) as CacheUpdate<any>
            )
          );

          // Update the recent chats list
          dispatch(
            api.util.updateQueryData(
              'getRecentChats' as never,
              undefined as never,
              ((draft: any) => {
                if (draft?.data?.threads) {
                  const threadIndex = draft.data.threads.findIndex(
                    (t: any) => t.id === threadId
                  );
                  if (threadIndex !== -1) {
                    const thread = draft.data.threads[threadIndex];
                    thread.lastMessage = newMessage.data.content;
                    thread.updatedAt = new Date().toISOString();
                    // Move to top of the list
                    draft.data.threads.splice(threadIndex, 1);
                    draft.data.threads.unshift(thread);
                  }
                }
                return draft;
              }) as CacheUpdate<any>
            )
          );
        } catch (error) {
          // Revert the optimistic update on error
          patchResult.undo();
          console.error('Failed to send message:', error);
        }
      }
    }),
    getRecentChats: builder.query<RecentChatsResponse, void>({
      query: () => ({
        url: END_POINTS.CHAT.RECENT_CHATS,
        method: 'GET'
      }),
      // Cache the data for 30 seconds
      keepUnusedDataFor: 30
    }),
    getContacts: builder.query<
      ContactsResponse,
      {
        name?: string;
        page?: number;
        limit?: number;
        orderBy?: string;
        orderDirection?: string;
      }
    >({
      query: ({
        name = '',
        page = 1,
        limit = 10,
        orderBy = 'recentActivity',
        orderDirection = 'DESC'
      }) => ({
        url: `chat/contacts?name=${encodeURIComponent(
          name
        )}&page=${page}&limit=${limit}&orderBy=${orderBy}&orderDirection=${orderDirection}`,
        method: 'GET'
      }),
      keepUnusedDataFor: 30
    }),
    getOrCreateThread: builder.mutation<
      ThreadResponse,
      {
        participantIds: string[];
        timezone: string;
      }
    >({
      query: (body) => ({
        url: 'chat/threads',
        method: 'POST',
        body: {
          type: 'direct',
          ...body
        }
      })
    }),
    getThreadMessages: builder.query<
      MessagesResponse,
      {
        threadId: string;
        limit?: number;
        beforeCreatedAt?: string;
        afterCreatedAt?: string;
      }
    >({
      query: ({ threadId, limit = 10, beforeCreatedAt, afterCreatedAt }) => {
        const params = new URLSearchParams();

        if (limit) params.append('limit', limit.toString());
        if (beforeCreatedAt) params.append('beforeCreatedAt', beforeCreatedAt);
        if (afterCreatedAt) params.append('afterCreatedAt', afterCreatedAt);

        const queryString = params.toString();
        return {
          url: `chat/threads/${threadId}/messages${
            queryString ? `?${queryString}` : ''
          }`,
          method: 'GET'
        };
      },
      // Cache messages for 30 seconds
      keepUnusedDataFor: 30
    }),
    // Mark messages as seen
    markMessagesAsSeen: builder.mutation<
      MarkMessagesSeenResponse,
      MarkMessagesSeenRequest
    >({
      query: ({ threadId, messageIds }) => ({
        url: `chat/threads/${threadId}/messages/seen`,
        method: 'POST',
        body: messageIds ? { messageIds } : {}
      }),
      // Update the cache to mark messages as read
      async onQueryStarted(
        { threadId, messageIds },
        { dispatch, queryFulfilled }
      ) {
        // Optimistic update: Mark messages as read in the cache
        const patchResult = dispatch(
          api.util.updateQueryData(
            'getThreadMessages' as never,
            { threadId } as never,
            ((draft: any) => {
              if (draft?.data?.messages) {
                draft.data.messages = draft.data.messages.map(
                  (msg: Message) => {
                    // If messageIds is provided, only update those messages
                    // Otherwise, mark all messages in the thread as read
                    if (!messageIds || messageIds.includes(msg.id)) {
                      return {
                        ...msg,
                        readBy: [
                          ...new Set([...(msg.readBy || []), 'current-user-id'])
                        ]
                      };
                    }
                    return msg;
                  }
                );
              }
              return draft;
            }) as any
          )
        );

        try {
          await queryFulfilled;
          // Update recent chats to remove unread indicator
          dispatch(
            api.util.updateQueryData(
              'getRecentChats' as never,
              undefined as never,
              ((draft: any) => {
                if (draft?.data?.threads) {
                  const thread = draft.data.threads.find(
                    (t: any) => t.id === threadId
                  );
                  if (thread) {
                    thread.hasUnread = false;
                  }
                }
                return draft;
              }) as any
            )
          );
        } catch (error) {
          // Revert the optimistic update on error
          patchResult.undo();
          console.error('Failed to mark messages as seen:', error);
        }
      }
    })
  })
});

// Export the enhanced API with the new endpoints
export const {
  useGetRecentChatsQuery,
  useLazyGetRecentChatsQuery,
  useGetContactsQuery,
  useLazyGetContactsQuery,
  useGetOrCreateThreadMutation,
  useGetThreadMessagesQuery,
  useLazyGetThreadMessagesQuery,
  useSendMessageMutation,
  useMarkMessagesAsSeenMutation
} = enhancedApi;

// Export the new interfaces
export type { MarkMessagesSeenRequest, MarkMessagesSeenResponse };
